import {
  getColumns,
  TableBody,
  TableBulkItemHeader,
  TableBulkItemRow,
  TableHeaderItem
} from "collections/Table";
import { ConnectionStatusIcon } from "elements";
import {
  useFirmwareUpdateInfoQuery,
  useUserByIdQuery
} from "generated/graphql";
import { Link, useQueryParamString } from "polyrouter";
import React, { ReactNode, useEffect, useRef, useState } from "react";
import { TABLES, ClientOptionType } from "store/userMeta";
import { useTranslation } from "utilities/i18n";
import { handleNullFields } from "utilities/stringUtils";
import {
  getUserConnectedDevices,
  deviceWithClientType
} from "routes/Manage/DeviceUsers/Shared/utils/deviceUserUtils";
import { LENS_DESKTOP, LENS_MOBILE } from "Constants";
import { hasValue } from "utilities/filters/filters";

export interface UserDeviceRowProps {
  setFirmwareUpdateInfo: React.Dispatch<
    React.SetStateAction<{
      count: number;
      loading: boolean;
    }>
  >;
  lastItem: boolean;
  connected: boolean | null;
  name: string | null;
  id: string;
  softwareVersion: string | null;
  site: {
    name: string;
  } | null;
  hardwareRevision: string | null;
  lastDetected: string;
  shownColumns: Array<string>;
  clients: Array<string>;
  clientOptions: { [x: string]: string[] };
}

export const UserDeviceTable: React.FC<{
  setFirmwareUpdateInfo: React.Dispatch<
    React.SetStateAction<{
      count: number;
      loading: boolean;
    }>
  >;
}> = ({ setFirmwareUpdateInfo }) => {
  const { t } = useTranslation();
  const { COLUMNS } = TABLES.USER_DEVICES;
  const shownColumns = Object.values(COLUMNS);
  const [user_id] = useQueryParamString("user_id");
  const { data: userData, fetching: userFetching } = useUserByIdQuery({
    variables: {
      id: user_id as string
    }
  });
  const defaultFilters = {
    [COLUMNS.CLIENT]: ""
  };
  const [filters, setFilters] = useState<Record<string, string>>(
    defaultFilters
  );
  const clientOptions = {
    [t("DESKTOP")]: [LENS_DESKTOP],
    [t("MOBILE")]: [LENS_MOBILE],
    [t("DESKTOP_AND_MOBILE")]: [LENS_DESKTOP, LENS_MOBILE]
  };
  // Options that are appliccale for user's device list
  let clientFilterOptions: ClientOptionType = {};
  const connectedDevices = getUserConnectedDevices(
    userData?.user.user?.connections,
    clientOptions[filters[COLUMNS.CLIENT]]
  );
  const devicesRef = useRef(connectedDevices);
  if (!userFetching) {
    devicesRef.current = connectedDevices;
    // Update client filter options based on user device clients
    connectedDevices?.forEach(device => {
      if (clientOptions) {
        let key = Object.keys(clientOptions).find(
          key =>
            JSON.stringify(clientOptions[key]) ===
            JSON.stringify(device.clients)
        );
        if (key) clientFilterOptions[key] = clientOptions[key];
      }
    });
  }
  useEffect(() => {
    if (
      !userFetching &&
      devicesRef.current &&
      devicesRef.current.length === 0
    ) {
      setFirmwareUpdateInfo(firmwareInfo => ({
        ...firmwareInfo,
        loading: false
      }));
    }
  }, [userFetching, setFirmwareUpdateInfo, devicesRef]);

  const columnTranslations = {
    [COLUMNS.NAME]: t("DEVICE_NAME"),
    [COLUMNS.LAST_DETECTED]: t("LAST_DETECTED"),
    [COLUMNS.SITE]: t("SITE"),
    [COLUMNS.VERSION]: t("VERSION"),
    [COLUMNS.APP_VERSION]: t("CLIENT_APP_VERSION"),
    [COLUMNS.CLIENT]: t("CLIENT")
  };
  const filterOptions = {
    [COLUMNS.CLIENT]: Object.keys(clientFilterOptions)
  };
  const updateFilters = (field: string, value: string) => {
    setFilters({
      ...filters,
      ...{ [field]: value }
    });
  };
  const hasActiveFilter = Object.keys(filters).some(filter =>
    hasValue(filters, filter)
  );
  const getTableHeaders = (column: string, title: string) => {
    const filterValue = filters[column];
    const filterable = filterValue !== undefined;
    const filter = filterable
      ? {
          filterValue,
          filterOptions: filterOptions[column],
          onFilter: updateFilters
        }
      : undefined;
    return (
      <TableHeaderItem
        fieldName={column}
        title={title}
        key={column}
        filter={filter}
      />
    );
  };

  const shown = getColumns(shownColumns, columnTranslations);
  const columnCount = shown.length + 1;

  return (
    <>
      <table className="table" data-testid="associated-devices">
        <TableBulkItemHeader
          checked={false}
          onCheck={() => {}}
          className="card"
          showChecks={false}
          onClearFilters={() => setFilters(defaultFilters)}
          showClearFilters={hasActiveFilter}
        >
          <th className="bulk-item">
            <ConnectionStatusIcon connected={true} className="m-0" />
          </th>
          {shown.map(column => getTableHeaders(column.key, column.title))}
        </TableBulkItemHeader>
        <TableBody
          loading={userFetching}
          cols={columnCount}
          loaderClassName="sm"
          accommodateScrollbar
        >
          {devicesRef.current && devicesRef.current.length > 0 ? (
            devicesRef.current.map(
              (device: deviceWithClientType, i: number) => {
                return (
                  <UserDeviceRow
                    {...device}
                    key={i}
                    setFirmwareUpdateInfo={setFirmwareUpdateInfo}
                    lastItem={i === devicesRef.current!.length - 1}
                    shownColumns={shown.map(column => column.key)}
                    clientOptions={clientFilterOptions}
                  />
                );
              }
            )
          ) : (
            <tr>
              <td colSpan={columnCount} className="p-15 text-center">
                <h6 data-testid="no-devices-found">{t("NO_DEVICES")}</h6>
              </td>
            </tr>
          )}
        </TableBody>
      </table>
    </>
  );
};

export const UserDeviceRow: React.FC<UserDeviceRowProps> = ({
  connected,
  name,
  id,
  lastItem,
  site,
  lastDetected,
  softwareVersion,
  clients,
  setFirmwareUpdateInfo,
  shownColumns,
  clientOptions
}) => {
  const { t } = useTranslation();
  const { data, fetching } = useFirmwareUpdateInfoQuery({
    variables: {
      id
    }
  });

  const { COLUMNS } = TABLES.USER_DEVICES;
  const updateAvailable = data?.firmwareUpdateInfo.isUpdateAvailable;

  useEffect(() => {
    if (lastItem && !fetching) {
      setFirmwareUpdateInfo(firmwareInfo => ({
        ...firmwareInfo,
        loading: false
      }));
    }
  }, [setFirmwareUpdateInfo, lastItem, fetching]);

  useEffect(() => {
    if (updateAvailable) {
      setFirmwareUpdateInfo(firmwareInfo => ({
        ...firmwareInfo,
        count: firmwareInfo.count + 1
      }));
    }
  }, [updateAvailable, setFirmwareUpdateInfo]);

  const getTableContent = (column: string) => {
    let content: ReactNode = null;
    switch (column) {
      case COLUMNS.NAME:
        content = (
          <Link to={`/manage/inventory/details/${id}`}>
            {handleNullFields(name)}
          </Link>
        );
        break;
      case COLUMNS.LAST_DETECTED:
        content = lastDetected
          ? new Date(Date.parse(lastDetected)).toLocaleDateString()
          : handleNullFields(lastDetected);
        break;
      case COLUMNS.SITE:
        content = handleNullFields(site?.name);
        break;
      case COLUMNS.APP_VERSION:
        content = handleNullFields(softwareVersion);
        break;
      case COLUMNS.CLIENT:
        let result: string | undefined = "";
        if (clientOptions)
          result = Object.keys(clientOptions).find(
            key =>
              JSON.stringify(clientOptions[key]) === JSON.stringify(clients)
          );
        content = (
          <span data-testid="client-info">{handleNullFields(result)}</span>
        );
        break;
      case COLUMNS.VERSION:
        content = updateAvailable ? (
          <span className="update-available" data-testid="firmware-update-info">
            {t("UPDATE_AVAILABLE")}
          </span>
        ) : (
          <span data-testid="firmware-update-info">{t("UP_TO_DATE")}</span>
        );
        break;
    }

    return <td key={column}>{content}</td>;
  };

  if (fetching) {
    return <></>;
  }

  return (
    <TableBulkItemRow checked={false} showChecks={false} onCheck={() => {}}>
      <td className="bulk-item">
        <ConnectionStatusIcon connected={connected} className="m-0" />
      </td>
      {shownColumns.map(getTableContent)}
    </TableBulkItemRow>
  );
};
